import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  DollarSign,
  FileText,
  Heart,
  Info,
  Save,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import ChatWidget from "@/components/ChatWidget";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { getStatusMeta } from "@/lib/visaStatus";

export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lookup, setLookup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingTrip, setSavingTrip] = useState(false);

  useEffect(() => {
    api
      .get(`/lookups/${id}`)
      .then((r) => setLookup(r.data))
      .catch(() => toast.error("Lookup not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const saveTrip = async () => {
    if (!user) {
      toast.info("Please sign in to save trips");
      navigate("/login");
      return;
    }
    setSavingTrip(true);
    try {
      await api.post("/trips", { lookup_id: id });
      toast.success("Trip saved to your dashboard");
    } catch (e) {
      toast.error("Could not save trip");
    } finally {
      setSavingTrip(false);
    }
  };

  const goPremium = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    navigate(`/report/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-grid">
        <Navbar />
        <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
          <Skeleton className="h-12 w-2/3" />
          <Skeleton className="mt-4 h-64 w-full" />
          <Skeleton className="mt-4 h-96 w-full" />
        </main>
      </div>
    );
  }

  if (!lookup) return null;

  const r = lookup.result;
  const meta = getStatusMeta(r.visa_status);
  const StatusIcon = meta.icon;

  return (
    <div className="min-h-screen bg-grid">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:py-12">
        <button
          onClick={() => navigate("/checker")}
          data-testid="back-to-checker"
          className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> New search
        </button>

        {/* Header */}
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-sm text-slate-500">
                <span className="font-mono font-medium">{lookup.nationality}</span> →{" "}
                <span className="font-mono font-medium">{lookup.destination}</span> ·{" "}
                {lookup.purpose} · {lookup.duration_days} days
              </div>
              <h1 className="mt-2 font-heading text-3xl font-bold sm:text-4xl">
                {lookup.nationality_name} → {lookup.destination_name}
              </h1>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={saveTrip}
                disabled={savingTrip}
                data-testid="save-trip-btn"
              >
                <Save className="mr-2 h-4 w-4" /> Save trip
              </Button>
              <Button
                onClick={goPremium}
                data-testid="premium-report-btn"
                className="bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
              >
                <Sparkles className="mr-2 h-4 w-4" /> Premium report
              </Button>
            </div>
          </div>

          <div
            data-testid="visa-status-badge"
            className={`mt-6 inline-flex items-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-semibold ${meta.className}`}
          >
            <StatusIcon className="h-5 w-5" />
            {r.visa_status_label || meta.label}
          </div>

          {r.summary && (
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-700">{r.summary}</p>
          )}
        </div>

        {/* Key stats */}
        <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={Clock}
            label="Max Stay"
            value={r.max_stay_days ? `${r.max_stay_days} days` : "—"}
          />
          <StatCard
            icon={Calendar}
            label="Passport Validity"
            value={r.passport_validity_months ? `${r.passport_validity_months} months` : "—"}
          />
          <StatCard icon={Clock} label="Processing Time" value={r.processing_time || "—"} />
          <StatCard
            icon={DollarSign}
            label="Total Fees"
            value={`$${(r.government_fee_usd || 0) + (r.service_fee_usd || 0)}`}
          />
        </section>

        {/* Documents */}
        <section className="mt-6 grid gap-6 lg:grid-cols-2">
          <InfoBlock icon={FileText} title="Required Documents">
            <ul className="space-y-2">
              {(r.documents_required || []).map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  {d}
                </li>
              ))}
            </ul>
          </InfoBlock>

          <InfoBlock icon={Heart} title="Health & Vaccinations">
            {r.health_requirements && r.health_requirements.length > 0 ? (
              <ul className="space-y-2">
                {r.health_requirements.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <Heart className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
                    {h}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">No special health requirements listed.</p>
            )}
          </InfoBlock>

          <InfoBlock icon={CheckCircle2} title="Application Steps">
            <ol className="space-y-2">
              {(r.application_steps || []).map((s, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-700">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                    {i + 1}
                  </span>
                  {s}
                </li>
              ))}
            </ol>
          </InfoBlock>

          <InfoBlock icon={Building2} title="Embassy / Consulate">
            {r.embassy ? (
              <div className="space-y-1 text-sm text-slate-700">
                <div className="font-medium">{r.embassy.name}</div>
                {r.embassy.website && (
                  <a
                    href={r.embassy.website}
                    target="_blank"
                    rel="noreferrer"
                    className="block text-teal-600 hover:underline"
                  >
                    {r.embassy.website}
                  </a>
                )}
                {r.embassy.phone && (
                  <div className="font-mono text-xs text-slate-500">{r.embassy.phone}</div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Contact information unavailable.</p>
            )}
          </InfoBlock>
        </section>

        {r.important_notes && r.important_notes.length > 0 && (
          <section className="mt-6 rounded-3xl border border-amber-200 bg-amber-50 p-6">
            <div className="flex items-start gap-3">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div>
                <h3 className="font-heading font-semibold text-amber-900">Important Notes</h3>
                <ul className="mt-2 space-y-1.5">
                  {r.important_notes.map((n, i) => (
                    <li key={i} className="text-sm text-amber-900/80">
                      • {n}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        <div className="mt-6 flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-500">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <p>
            AI-generated guidance for informational use only. Rules change frequently — always
            confirm with the official embassy or government portal of your destination before
            traveling.
          </p>
        </div>
      </main>
      <ChatWidget
        context={`${lookup.nationality_name} traveler to ${lookup.destination_name} for ${lookup.purpose}, ${lookup.duration_days} days. Status: ${r.visa_status_label}.`}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-2 font-heading text-xl font-semibold">{value}</div>
    </div>
  );
}

function InfoBlock({ icon: Icon, title, children }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white">
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="font-heading text-lg font-semibold">{title}</h3>
      </div>
      {children}
    </div>
  );
}
